// apps/ers/src/app/modules/ers-leave-management/services/leave-data.service.ts

import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, tap, finalize } from 'rxjs/operators';
import { LeaveRequest, LeaveBalance, Holiday, HourlyRequest } from '../models/leave.model';
import { LeaveService } from './leave.service';

@Injectable({ providedIn: 'root' })
export class LeaveDataService {
  private readonly leaveService = inject(LeaveService);

  // ─── State Subjects ───────────────────────────────────────────────────────
  private readonly myLeaveRequestsSubject   = new BehaviorSubject<LeaveRequest[]>([]);
  private readonly teamLeaveRequestsSubject = new BehaviorSubject<LeaveRequest[]>([]);
  private readonly leaveBalancesSubject     = new BehaviorSubject<LeaveBalance[]>([]);
  private readonly holidaysSubject          = new BehaviorSubject<Holiday[]>([]);
  private readonly hourlyRequestsSubject    = new BehaviorSubject<HourlyRequest[]>([]);
  private readonly loadingSubject           = new BehaviorSubject<boolean>(false);

  // ─── Public Observables ───────────────────────────────────────────────────
  readonly myLeaveRequests$   = this.myLeaveRequestsSubject.asObservable();
  readonly teamLeaveRequests$ = this.teamLeaveRequestsSubject.asObservable();
  readonly leaveBalances$     = this.leaveBalancesSubject.asObservable();
  readonly holidays$          = this.holidaysSubject.asObservable();
  readonly hourlyRequests$    = this.hourlyRequestsSubject.asObservable();
  readonly loading$           = this.loadingSubject.asObservable();

  // ─── Generic Loader (DRY) ─────────────────────────────────────────────────
  // Eliminates repeated loading → tap → finalize pattern

  private loadWithState<T>(source$: Observable<T>, subject: BehaviorSubject<T>): Observable<T> {
    this.loadingSubject.next(true);
    return source$.pipe(
      tap(data => subject.next(data)),
      // LeaveService already showed the snackbar; here we just ensure the
      // loading spinner resets and the subject keeps its last known value.
      catchError(() => of(subject.getValue())),
      finalize(() => this.loadingSubject.next(false)),
    );
  }

  // ─── Loaders ──────────────────────────────────────────────────────────────

  loadMyLeaveRequests(): Observable<LeaveRequest[]> {
    return this.loadWithState(this.leaveService.getMyLeaveRequests(), this.myLeaveRequestsSubject);
  }

  loadTeamLeaveRequests(): Observable<LeaveRequest[]> {
    return this.loadWithState(this.leaveService.getTeamLeaveRequests(), this.teamLeaveRequestsSubject);
  }

  loadLeaveBalances(): Observable<LeaveBalance[]> {
    return this.loadWithState(this.leaveService.getMyLeaveBalance(), this.leaveBalancesSubject);
  }

  loadHolidays(): Observable<Holiday[]> {
    return this.loadWithState(this.leaveService.getHolidays(), this.holidaysSubject);
  }

  loadHourlyRequests(): Observable<HourlyRequest[]> {
    return this.loadWithState(this.leaveService.getMyHourlyRequests(), this.hourlyRequestsSubject);
  }

  // ─── Bulk Operations ──────────────────────────────────────────────────────

  refreshAll(): void {
    this.loadMyLeaveRequests().subscribe();
    this.loadLeaveBalances().subscribe();
    this.loadHolidays().subscribe();
    this.loadHourlyRequests().subscribe();
  }

  clearCache(): void {
    this.myLeaveRequestsSubject.next([]);
    this.teamLeaveRequestsSubject.next([]);
    this.leaveBalancesSubject.next([]);
    this.holidaysSubject.next([]);
    this.hourlyRequestsSubject.next([]);
  }
}